/**
 * Card Style Variants
 *
 * Premium card styles for different content types.
 * Provides visual differentiation and improved hierarchy with modern aesthetics.
 */

// Card style configurations by type - Premium Design
export const cardStyles = {
  // RSS Content cards - emphasize discovery with indigo
  rss: {
    border: "1px solid rgba(99, 102, 241, 0.1)",
    background: "linear-gradient(135deg, #ffffff 0%, #fafbff 100%)",
    boxShadow: "0 2px 8px rgba(99, 102, 241, 0.04)",
    "&:hover": {
      border: "1px solid rgba(99, 102, 241, 0.2)",
      boxShadow: "0 12px 32px rgba(99, 102, 241, 0.12)",
    },
  },

  // Draft cards - emphasize action needed with amber
  draft: {
    border: "1px solid rgba(245, 158, 11, 0.1)",
    background: "linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)",
    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.04)",
    "&:hover": {
      border: "1px solid rgba(245, 158, 11, 0.2)",
      boxShadow: "0 12px 32px rgba(245, 158, 11, 0.12)",
    },
  },

  // Posted cards - emphasize success with emerald
  posted: {
    border: "1px solid rgba(16, 185, 129, 0.1)",
    background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.04)",
    "&:hover": {
      border: "1px solid rgba(16, 185, 129, 0.2)",
      boxShadow: "0 12px 32px rgba(16, 185, 129, 0.12)",
    },
  },

  // Rejected cards - subtle de-emphasis with rose
  rejected: {
    border: "1px solid rgba(239, 68, 68, 0.1)",
    background: "linear-gradient(135deg, #ffffff 0%, #fff1f2 100%)",
    boxShadow: "0 2px 8px rgba(239, 68, 68, 0.04)",
    opacity: 0.9,
    "&:hover": {
      opacity: 1,
      border: "1px solid rgba(239, 68, 68, 0.2)",
      boxShadow: "0 12px 32px rgba(239, 68, 68, 0.1)",
    },
  },

  // Default/neutral style
  default: {
    border: "1px solid rgba(0, 0, 0, 0.04)",
    background: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    "&:hover": {
      border: "1px solid rgba(0, 0, 0, 0.08)",
      boxShadow: "0 12px 32px rgba(0, 0, 0, 0.08)",
    },
  },
};

// Status badge configurations with premium styling
export const statusConfig = {
  New: {
    color: "info",
    bgColor: "rgba(99, 102, 241, 0.1)",
    textColor: "#6366f1",
    icon: "fiber_new",
    label: "New",
  },
  "Pending Approval": {
    color: "warning",
    bgColor: "rgba(245, 158, 11, 0.1)",
    textColor: "#f59e0b",
    icon: "pending",
    label: "Pending Review",
  },
  "Good to go": {
    color: "success",
    bgColor: "rgba(16, 185, 129, 0.1)",
    textColor: "#10b981",
    icon: "check_circle",
    label: "Ready",
  },
  Posted: {
    color: "success",
    bgColor: "rgba(16, 185, 129, 0.1)",
    textColor: "#10b981",
    icon: "check_circle",
    label: "Published",
  },
  Rejected: {
    color: "error",
    bgColor: "rgba(239, 68, 68, 0.1)",
    textColor: "#ef4444",
    icon: "cancel",
    label: "Declined",
  },
  Processing: {
    color: "secondary",
    bgColor: "rgba(100, 116, 139, 0.1)",
    textColor: "#64748b",
    icon: "hourglass_empty",
    label: "Processing",
  },
};

// Get card style by content type
export function getCardStyle(type) {
  return cardStyles[type] || cardStyles.default;
}

// Get status configuration
export function getStatusConfig(status) {
  return statusConfig[status] || statusConfig.New;
}

// Common card base styles - Premium Design
export const cardBaseStyles = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  borderRadius: "20px",
  overflow: "hidden",
  cursor: "pointer",
  position: "relative",
  "&:hover": {
    transform: "translateY(-6px)",
  },
  "&:active": {
    transform: "translateY(-2px)",
  },
  "@media (hover: none)": {
    "&:hover": {
      transform: "none",
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  },
};

// Enhanced badge styles - Premium Design
export const enhancedBadgeStyles = (status) => {
  const config = getStatusConfig(status);
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    px: 1.5,
    py: 0.5,
    borderRadius: "10px",
    backgroundColor: config.bgColor,
    color: config.textColor,
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  };
};

// Platform badge styles
export const platformBadgeStyles = {
  linkedin: {
    bgColor: "rgba(10, 102, 194, 0.1)",
    textColor: "#0a66c2",
    icon: "work",
  },
  x: {
    bgColor: "rgba(29, 161, 242, 0.1)",
    textColor: "#1da1f2",
    icon: "close",
  },
  threads: {
    bgColor: "rgba(0, 0, 0, 0.05)",
    textColor: "#000000",
    icon: "chat",
  },
  instagram: {
    bgColor: "rgba(228, 64, 95, 0.1)",
    textColor: "#e4405f",
    icon: "camera_alt",
  },
};

// Get platform badge style
export function getPlatformBadgeStyle(platform) {
  return platformBadgeStyles[platform?.toLowerCase()] || null;
}
